import type { IMetadata } from '@adloid-technologies/discover-sdk';

/**
 * GMC Acadia (linkId 2377, project 59) — Denali and AT4 trims.
 *
 * Converted from the 1.0 repo's `src/metadata/data/metadata-2377.ts`
 * (`appData.visualiser.models[0]`, array-shaped) into the 2.0 object-keyed
 * `IMetadata` shape.
 *
 * Unlike Hummer (2382) and Groove (2386), 1.0 never reuses a configurationId
 * across Denali and AT4 — every colour, feature, accessory, interaction and
 * hotspot has its own distinct id per trim (0 shared ids, verified against the
 * source). So collect()'s consolidate-or-split fingerprinting never merges
 * anything here; every entity naturally lands as a single-`relevantVariants`
 * entry. The one place that still needs explicit handling is `hotspots`,
 * which the SDK types as one Record<ICameraNames, IHotspot> for the WHOLE
 * model (confirmed against Silverado, 18 variants, one hotspots block) — built
 * here from the default Denali variant's three seat rows; AT4's separately-id'd
 * rows have no schema slot to occupy and are redundant under it.
 *
 * `dependsOn` on the Seats Folding interaction: 1.0's `{id:'boot', ...}`
 * object becomes the 2.0 array-of-configurationId form (matches Tahoe's
 * existing suspension-lift interaction, the only other 2.0 use of the field).
 *
 * Generated — see the conversion notes in ui-metadata.ts.
 */
export const metadata: IMetadata = {
  models: {
    Acadia: {
      id: 'Acadia',
      lightingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      postProcessingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      backgroundIdentifer: 'dome',
      variants: {
        denali: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '5726',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '5729',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '5737',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '5529',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        at4: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '5726',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '5721',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '5737',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '5529',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
            {
              id: 'alloys',
              sku: '5732',
              projectId: '59',
              position: [0, 0, 0],
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
      },
      backgrounds: {
        g0ZA968_RpUd: {
          isDefault: true,
          sku: '5529',
          project: '59',
        },
        g0ZA968_RpUe: {
          isDefault: false,
          sku: '5526',
          project: '59',
        },
      },
      ambientLighting: {
        ambientColors: '',
        ambientLightings: {},
        relevantVariants: [],
      },
      colors: {
        exterior: {
          '7eez7nplog7a': {
            sku: '5728',
            project: '59',
            relevantVariants: [
              'denali',
            ],
          },
          ruym5z4zk7bp: {
            sku: '5735',
            project: '59',
            relevantVariants: [
              'denali',
            ],
          },
          dtjmzz3bsfrl: {
            sku: '5733',
            project: '59',
            relevantVariants: [
              'denali',
            ],
          },
          svfkt34o1vbs: {
            sku: '5731',
            project: '59',
            relevantVariants: [
              'denali',
            ],
          },
          '2h6rvjratd39': {
            sku: '5724',
            project: '59',
            relevantVariants: [
              'denali',
            ],
          },
          ys6fc5ecm0gr: {
            sku: '5737',
            project: '59',
            relevantVariants: [
              'denali',
            ],
          },
          '7eez7neplog7a': {
            sku: '5728',
            project: '59',
            relevantVariants: [
              'at4',
            ],
          },
          ruym5ze4zk7bp: {
            sku: '5735',
            project: '59',
            relevantVariants: [
              'at4',
            ],
          },
          dtjmezz3bsfrl: {
            sku: '5733',
            project: '59',
            relevantVariants: [
              'at4',
            ],
          },
          htk1s9thjb4s: {
            sku: '5739',
            project: '59',
            relevantVariants: [
              'at4',
            ],
          },
          '2h6rvejratd39': {
            sku: '5724',
            project: '59',
            relevantVariants: [
              'at4',
            ],
          },
          ys6fcee5ecm0gr: {
            sku: '5737',
            project: '59',
            relevantVariants: [
              'at4',
            ],
          },
        },
        interior: {
          '5vktfxuscn42': {
            sku: '5729',
            project: '59',
            relevantVariants: [
              'denali',
            ],
            notCompatibleExteriorColors: [],
          },
          xyus3yrafri2: {
            sku: '5734',
            project: '59',
            relevantVariants: [
              'denali',
            ],
            notCompatibleExteriorColors: [],
          },
          hp43ibg9l4ft: {
            sku: '5721',
            project: '59',
            relevantVariants: [
              'at4',
            ],
            notCompatibleExteriorColors: [],
          },
        },
      },
      features: {
        av8ukajwzjsm: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_20_PNMA Alloy',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: '20 inch Alloy',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '56b99wbs39ki': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_Denali Grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Denali Grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        slfnh3hjd6s8: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'Headlamps',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Headlamp',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        e31dagcaee09: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_Taillamp',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Taillamp',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            smjhed8evi5i: { add: false, snapToLastFrame: true },
          },
        },
        e31dagcaeee09: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_ORVM',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'ORVM',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            nw7ayiomff7o: { add: false, snapToLastFrame: true },
          },
        },
        e31dagccaeee09: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'Power Liftgate',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '3ofodapu2ddp': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_HUD',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'HUD',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        muey3bbku37k: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'First row and second row seats',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        z7m52iyopf8c: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_Info screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'GMC Info screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        awla0dgc0vgs: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_Driver Info system',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: '11 inch DIC',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        h0dy3khszwvy: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_Bose Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Bose Premium Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            nw7ayiomff7o: { add: false, snapToLastFrame: true },
          },
        },
        vsefclj6vozz: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'Ambient Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            nw7ayiomff7o: { add: false, snapToLastFrame: true },
          },
        },
        '7ncmf61zbg9j': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_360 Camera',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: '360 Camera',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        jh03x1qu9di0: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_IRVM',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'IRVM',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        qesaxf0nj162: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_2nd row folding',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: '2nd row Touch flat folding',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        qesaxfr0nj162: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'denali',
          ],
          messages: [
            {
              message: 'SHADER_3rd row folding',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: true,
            },
            {
              message: '3rd row Power Folding',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: true,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        et1jiatqexbt: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'SHADER_18_Wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: '18inch Wheel',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        kh1xk6tsachv: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'Dark Accented Exterior AT4 ',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        n9uci5v0q0no: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'Night RIder AT4 Grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_AT4_grill',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '6jn6rb1ajk00': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'SHADER_Roof_Rail',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Outside mirror and roof Rail',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        fzpdk9bjmbo4: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'SHADER_Exhaust',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Dual Exhaust',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '1m71ax5f4id6': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'Off-Road Suspension',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        thexucnyzlqx: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'SHADER_Skid_tow_hook',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Skid plate and Tow Hooks',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        fz9wua33l4wm: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'SHADER_Info screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'GMC Info screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        a5fob6prph1z: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'SHADER_Bose Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Bose Premium Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            q4efn17j0xpb: { add: false, snapToLastFrame: true },
          },
        },
        pro1ugk226q0: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'Dark Accented Interior AT4 ',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        sp46ano5o1g6: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'SHADER_8way seat',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: '8-way seat adjuster',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        po1vxwjvhsua: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'SHADER_6way seat',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: '6-way seat adjuster',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '10giu7b6ijpj': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'SHADER_Driver Info system',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: '11 inch DIC',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '10giu7fb6ijpj': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'SHADER_driver_mode',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Selectable Drive Mode',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '10giue7fb6ijpj': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'at4',
          ],
          messages: [
            {
              message: 'SHADER_heated_seats',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Heated Driver & front Passenger Seats',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
      },
      accessories: {
        '3w06jsi5oinw': {
          view: 'exterior',
          groupId: 'denali-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Alloy_20in_Denali_Default',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        whv4p5vjna3p: {
          view: 'exterior',
          groupId: 'denali-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Alloy_22in_Denali_Optional',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ALLOY',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        dpsosp5gxrx3: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Vertical_Cargo_Net',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Vertical_Cargo_Net',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Vertical_Cargo_Net',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            smjhed8evi5i: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        prnt5nsa6jxn: {
          view: 'exterior',
          groupId: 'denali-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Cargo_Organizer',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Cargo_Organizer',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Cargo_Organizer',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            smjhed8evi5i: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '9kq6x8627fks': {
          view: 'exterior',
          groupId: 'denali-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Premium_Cargo_Mat_with_Bins',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Premium_Cargo_Mat_with_Bins',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Premium_Cargo_Mat_with_Bins',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            smjhed8evi5i: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        isd8oqmgpfud: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Integrated_Cargo_Liner',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Integrated_Cargo_Liner',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Integrated_Cargo_Liner',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            smjhed8evi5i: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        d0b6pb4rgdcw: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Cargo_Shelf_with_Dividers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Cargo_Shelf_with_Dividers',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Cargo_Shelf_with_Dividers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            smjhed8evi5i: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        oe069xyvrqoq: {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_First_Row_Premium_All_Weather_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_First_Row_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_First_Row_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        h3ahwov6h753: {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Second_Row_Premium_All_Weather_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Second_Row_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Second_Row_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        an0ysv4lgrpq: {
          view: 'interior',
          groupId: 'denali-g2',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Third_Row_Premium_All_Weather_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Third_Row_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Third_Row_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            smjhed8evi5i: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ssenv85hajoq: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Assist _Steps_Shark_Skin_Metallic',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Assist _Steps_Shark_Skin_Metallic',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Assist _Steps_Shark_Skin_Metallic',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            nw7ayiomff7o: {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        izsin73px8pj: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Variant_Parts_Denali',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ogh3vezi7oc0: {
          view: 'exterior',
          groupId: 'denali-g3',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Std_Roof_Sheer_Dark_Atmosphere',
              identifiers: [
                'exterior-color',
              ],
              condition: '(5729) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'Std_Roof_After_Dark_Perforated',
              identifiers: [
                'exterior-color',
              ],
              condition: '(5734) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        gzamkqryq6wb: {
          view: 'exterior',
          groupId: 'denali-g3',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Sunroof_Sheer_Dark_Atmosphere',
              identifiers: [
                'exterior-color',
              ],
              condition: '(5729) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'Sunroof_After_Dark_Perforated',
              identifiers: [
                'exterior-color',
              ],
              condition: '(5734) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '62c2lgtcvtgb': {
          view: 'exterior',
          groupId: 'denali-g2',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: '3rd_Row_Mat_Denali_Default',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'denali',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        o0effisb7huz: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Vertical_Cargo_Net',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Vertical_Cargo_Net',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Vertical_Cargo_Net',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            cid63atonu3q: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        arv6xc8di0p2: {
          view: 'exterior',
          groupId: 'at4-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Cargo_Organizer',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Cargo_Organizer',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Cargo_Organizer',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            cid63atonu3q: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        z60cgrm95zsc: {
          view: 'exterior',
          groupId: 'at4-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Premium_Cargo_Mat_with_Bins',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Premium_Cargo_Mat_with_Bins',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Premium_Cargo_Mat_with_Bins',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            cid63atonu3q: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        k4u455dsgte4: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Integrated_Cargo_Liner',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Integrated_Cargo_Liner',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Integrated_Cargo_Liner',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            cid63atonu3q: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        xw46c5hhztat: {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Cargo_Shelf_with_Dividers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Cargo_Shelf_with_Dividers',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Cargo_Shelf_with_Dividers',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            cid63atonu3q: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '7ua1p3j98kiy': {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_First_Row_Premium_All_Weather_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_First_Row_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_First_Row_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        t09zdke9k241: {
          view: 'interior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Second_Row_Premium_All_Weather_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Second_Row_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Second_Row_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        x8m9byz4k9y4: {
          view: 'interior',
          groupId: 'at4-g1',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Third_Row_Premium_All_Weather_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Third_Row_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Third_Row_Floor_Liners',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            cid63atonu3q: {
              add: true,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8h02oi0oprfb': {
          view: 'exterior',
          groupId: '',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'ACC_Assist _Steps_Shark_Skin_Metallic',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'Camera_Assist _Steps_Shark_Skin_Metallic',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'Shader_Assist _Steps_Shark_Skin_Metallic',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {
            q4efn17j0xpb: {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        d3hm2vqzttar: {
          view: 'exterior',
          groupId: 'at4-g2',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Luxury_package',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '1z85m5ckyrut': {
          view: 'exterior',
          groupId: 'at4-g2',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Luxury_package',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        jackzeoq4kxc: {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Variant_Parts_AT4',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '2cw4gpca53so': {
          view: 'exterior',
          groupId: 'at4-g3',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Std_Roof_Forest_Storm',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        f85e14irfgjr: {
          view: 'exterior',
          groupId: 'at4-g3',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Sunroof_Forest_Storm',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '5rtjv1h7i4pw': {
          view: 'exterior',
          groupId: 'at4-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: '3rd_Row_Mat_AT4_Default',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'at4',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
      },
      packages: {
        a1lyjpkbd835: {
          groupId: 'at4-pkg0',
          accessoryIds: [
            '1z85m5ckyrut',
          ],
          dependencyPackages: [],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
          relevantVariants: [
            'at4',
          ],
        },
      },
      interactions: {
        nw7ayiomff7o: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 5.0385,
                beta: 1.448,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            'denali',
          ],
        },
        smjhed8evi5i: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'denali',
          ],
        },
        '7x0dl7w7ygmd': {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_SHADE_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_SHADE_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7049,
                beta: 0.115,
                radius: 3.2,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.7145,
                beta: 3.1212,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.7222,
                beta: 1.8799,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 4.7036,
                beta: 1.5732,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            'denali',
          ],
          // Sunroof isn't standard on this trim - only show/enable this
          // interaction once the user has actually applied the Panoramic
          // Sunroof accessory, instead of unconditionally per
          // relevantVariants+view.
          availableFor: {
            accessories: ['gzamkqryq6wb'],
          },
        },
        z50uy8f03jh7: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'denali',
          ],
        },
        '14eisbljp9pi': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'denali',
          ],
        },
        '1vqrly7ngbhv': {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7054,
                beta: 1.4258,
                radius: 3.9547,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'denali',
          ],
        },
        aq2x1j1r6h63: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SEAT_FOLD_ML_FOLD',
              identifiers: [
                'interior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'SEAT_FOLD_MR_FOLD',
              identifiers: [
                'interior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'SEAT_FOLD_RL_FOLD',
              identifiers: [
                'interior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'SEAT_FOLD_RR_FOLD',
              identifiers: [
                'interior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SEAT_FOLD_ML_UNFOLD',
              identifiers: [
                'interior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'SEAT_FOLD_MR_UNFOLD',
              identifiers: [
                'interior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'SEAT_FOLD_RL_UNFOLD',
              identifiers: [
                'interior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'SEAT_FOLD_RR_UNFOLD',
              identifiers: [
                'interior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'denali',
          ],
          dependsOn: [
            'smjhed8evi5i',
          ],
        },
        q4efn17j0xpb: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 5.0385,
                beta: 1.448,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            'at4',
          ],
        },
        cid63atonu3q: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'at4',
          ],
        },
        '8y1ygewkzqwp': {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_SHADE_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_SHADE_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7049,
                beta: 0.115,
                radius: 3.2,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.7145,
                beta: 3.1212,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.7222,
                beta: 1.8799,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 4.7036,
                beta: 1.5732,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            'at4',
          ],
          // Sunroof isn't standard on this trim - only show/enable this
          // interaction once the user has actually applied the Panoramic
          // Sunroof accessory, instead of unconditionally per
          // relevantVariants+view.
          availableFor: {
            accessories: ['f85e14irfgjr'],
          },
        },
        xgeya76hww4y: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'at4',
          ],
        },
        z4fmgvbj6vl6: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'at4',
          ],
        },
        j9phsdy1vqre: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7054,
                beta: 1.4258,
                radius: 3.9547,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'at4',
          ],
        },
        aq2x1j1r6h61: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SEAT_FOLD_ML_FOLD',
              identifiers: [
                'interior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'SEAT_FOLD_MR_FOLD',
              identifiers: [
                'interior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'SEAT_FOLD_RL_FOLD',
              identifiers: [
                'interior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'SEAT_FOLD_RR_FOLD',
              identifiers: [
                'interior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SEAT_FOLD_ML_UNFOLD',
              identifiers: [
                'interior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'SEAT_FOLD_MR_UNFOLD',
              identifiers: [
                'interior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'SEAT_FOLD_RL_UNFOLD',
              identifiers: [
                'interior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'SEAT_FOLD_RR_UNFOLD',
              identifiers: [
                'interior-color',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'at4',
          ],
          dependsOn: [
            'cid63atonu3q',
          ],
        },
      },
      hotspots: {
        exteriorCamera: {
          type: 'exterior',
          messages: {
            fov: 0.8,
            alpha: 2.02,
            beta: 1.47,
            radius: 5.7,
            lowerAlphaLimit: null,
            upperAlphaLimit: null,
            lowerBetaLimit: 0.1,
            upperBetaLimit: 1.6,
            lowerRadiusLimit: 3.2,
            upperRadiusLimit: 6.7,
            target: {
              x: 0,
              y: 0.65,
              z: 0,
            },
            isFovZoomModeOn: false,
          },
        },
        interiorCamera: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA',
            identifiers: [
              'common_exterior',
            ],
          },
        },
        interiorCamera_1: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA_2',
            identifiers: [
              'common_exterior',
            ],
          },
        },
        interiorCamera_2: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA_3',
            identifiers: [
              'common_exterior',
            ],
          },
        },
      },
      usdzPathWithVariable: '',
    },
  },
};
